<template>
  <detail-layout>
    <template slot="header">
      <base-header
        icon="user"
        :content="header"
      />
    </template>
    <template slot="main">
      <div class="form-wrapper">
        <el-form
          :model="form"
          :rules="rulesFlow"
          ref="ruleForm"
          class="flex flex-row w-full"
          autocomplete="off"
        >
          <div class="flex-1">
            <div class="lg:mb-4">
            </div>

            <div class="lg:mb-2" v-if="(role === 'AdminDistribuidor' || role === 'SuperAdminDistribuidor' || role === 'SuperAdministrador')">
              <div class="flex justify-between items-center">
                <div class="flex-1">
                  <AccessibleElSelect
                    v-model="form.clienteId"
                    prop="clienteId"
                    :options="clientesForAccessibleSelect"
                    :isDisabled="!isCreate"
                    id-prefix="configflow"
                  >
                    <template slot="label">
                      <base-obligatory-icon v-if="isCreate"/>
                      {{$t('app.client')}}
                    </template>
                  </AccessibleElSelect>
                </div>
                <base-button
                  v-if="!isCreate"
                  class="ml-4"
                  mode="primary"
                  :text="$t('app.detail')"
                  @click.prevent.stop="goDetailClient"
                  :loading="isGeneralLoading"
                />
              </div>
            </div>

            <div class="lg:mb-2">
              <div class="mandatory">
                <label class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon/>{{$t('configurations.name')}}
                  <el-form-item prop="Nombre">
                    <el-input
                      clearable
                      class="w-full"
                      v-model="form.Nombre"
                      maxlength='100'
                    />
                  </el-form-item>
                </label>
              </div>
            </div>
            
            <div class="lg:mb-2">
                <div class="mandatory">
                  <div class="flex items-center mb-1">
                    <label class="text-sm font-semibold text-secondary-600 block"><base-obligatory-icon/>{{$t('configflow.TemporizadorDias')}}</label>
                    <base-tooltip-icon size="sm" :content="tooltipTemporizadorDias" class="ml-2"/>
                  </div>
                  <el-form-item prop="TemporizadorDias">
                    <el-input
                      clearable
                      class="w-full"
                      v-model="form.TemporizadorDias"
                      min="0"
                      max="720"
                      maxlength="3"
                      type="number"
                    />
                  </el-form-item>
                </div>
            </div>

            <div class="mb-6">
              <label for="switch-activo" class="sr-only">
                {{$t('app.active')}}. {{$t('app.options')}}: {{$t('app.inactive')}} / {{$t('app.active')}}
              </label>
              <div class="flex flex-row items-center">
                <el-switch 
                  id="switch-activo"
                  :aria-label="$t('app.active')"
                  class="switch-text break-normal" 
                  v-model="form.Activo" 
                  :active-text="$t('app.active')">
                </el-switch>
              </div>
            </div>

            <div class="mb-6">
              <AccessibleElSelect
                v-model="form.idEstadoInicial"
                prop="idEstadoInicial"
                :options="tiposEstadosEntradaForAccessibleSelect"
                clearable
                id-prefix="configflow"
              >
                <template slot="label">
                  <base-obligatory-icon/>
                  {{$t('app.estado_origen')}}
                </template>
              </AccessibleElSelect>
            </div>

            <div class="mb-6">
              <AccessibleElSelect
                v-model="form.idEstadoDestino"
                prop="idEstadoDestino"
                :options="tiposEstadosSalidaForAccessibleSelect"
                :isDisabled="!this.estadoInicialSelected || this.estadoInicialSinDestino"
                clearable
                id-prefix="configflow"
              >
                <template slot="label">{{$t('app.estado_destino')}}</template>
              </AccessibleElSelect>
            </div>
            
            <div class="mb-6">
              <AccessibleElSelect
                v-model="form.EmpresaID"
                prop="EmpresaID"
                :options="companiesForAccessibleSelect"
                :isDisabled="disableSelectsGeneral"
                clearable
                id-prefix="configflow"
              >
                <template slot="label">{{$t('app.company')}}</template>
              </AccessibleElSelect>
            </div>
            
            <div class="mb-6">
              <AccessibleElSelect
                v-model="form.SedeID"
                prop="SedeID"
                :options="sedesForAccessibleSelect"
                :isDisabled="disableSelectsGeneral"
                clearable
                id-prefix="configflow"
              >
                <template slot="label">{{$t('app.location')}}</template>
              </AccessibleElSelect>
            </div>

            <div class="mb-6">
              <AccessibleElSelect
                v-model="form.OrganismoID"
                prop="OrganismoID"
                :options="organismosForAccessibleSelect"
                clearable
                :remote="true"
                :remoteMethod="remoteOrganismos"
                popperClass="wrap-select-popover"
                :loading="loadingOrganismos"
                id-prefix="configflow"
              >
                <template slot="label">
                  <div class="flex items-center gap-x-2">
                    {{$t('app.organismo')}}
                    <base-tooltip-icon size="sm" :content="'Escribe para iniciar la búsqueda'"/>
                  </div>
                </template>
              </AccessibleElSelect>
            </div>

            <div>
              <AccessibleElSelect
                v-model="form.grupoPropietario"
                prop="grupoPropietario"
                :options="listaGroupsGroupingOwner"
                :isDisabled="disableSelectsGeneral"
                :group="true"
                id-prefix="configflow"
              >
                <template slot="label">{{$t('notificationFilter.group_owner')}}</template>
              </AccessibleElSelect>
            </div>

          <base-collapsible-form 
            ref="baseCollapsibleCorreo" 
            class="mt-2 bg-white dark:bg-darkblue-900 rounded-lg border-solid border-primary-300 dark:border-darkblue-600 border pr-4 pl-4 mb-2">
            <template slot="headerRight">
              <h2 class="dark:text-darkblue-200 font-bold mt-4 mb-4 text-sm">{{$t('companies.group_alert')}}</h2>
            </template>
            <template slot="content">

            <div class="mb-6 mt-2">
              <label for="switch-correos-principales" class="sr-only">
                {{$t('configflow.EnviarCorreosPrincipales')}}. Opciones: {{$t('app.inactive')}} / {{$t('app.active')}}
              </label>
              <div class="flex flex-row items-center">
                <el-switch 
                  id="switch-correos-principales"
                  :aria-label="$t('configflow.EnviarCorreosPrincipales')"
                  class="switch-text break-normal" 
                  v-model="form.EnviarCorreosPrincipales" 
                  :active-text="$t('configflow.EnviarCorreosPrincipales')">
                </el-switch>
              </div>
            </div>

            <div class="mb-6">
              <label for="switch-correos-secundarios" class="sr-only">
                {{$t('configflow.EnviarCorreosSecundarios')}}. Opciones: {{$t('app.inactive')}} / {{$t('app.active')}}
              </label>
              <div class="flex flex-row items-center">
                <el-switch 
                  id="switch-correos-secundarios"
                  :aria-label="$t('configflow.EnviarCorreosSecundarios')"
                  class="switch-text break-normal" 
                  v-model="form.EnviarCorreosSecundarios" 
                  :active-text="$t('configflow.EnviarCorreosSecundarios')">
                </el-switch>
              </div>
            </div> 

            <div class="lg:mb-2">
              <div class="mandatory">
                <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('configflow.EmailGenerico')}}
                  <el-form-item prop="EmailGenerico">
                    <el-input
                      clearable
                      class="w-full"
                      v-model="form.EmailGenerico"
                      :maxlength='maxLengthCorreos'
                    />
                  </el-form-item>
                </label>
              </div>
            </div>

            <div class="lg:mb-2">
              <label class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon/>{{$t('app.mail_asunto')}}
                <el-form-item prop="AsuntoCorreo">
                  <el-input
                    clearable
                    class="w-full"
                    v-model="form.AsuntoCorreo"
                    maxlength='255'
                  />
                </el-form-item>
              </label>
            </div>

            <div class="lg:mb-2">
              <label class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon/>{{$t('app.mail_cuerpo')}}</label>
              <TextEditor
                ref="textEditorCuerpoCorreo"
                :contentDefault="contentTextEditorCuerpoCorreo"
                idDefault="CuerpoCorreo"
                class="mb-4"
                :tags="tagsCuerpoCorreo"
                />              
            </div>

            </template>
          </base-collapsible-form>

          </div>
        </el-form>
      </div>
    </template>
    <template slot="layout-extend">
      <base-footer>
        <template slot="actions">
          <base-button-responsive
            mode="secondary"
            :text="isCreate ? $t('app.cancel') : $t('app.back')"
            icon="cross"
            @click="goBack"
            :loading="isGeneralLoading"
          />
          <base-button-responsive
            mode="primary"
            :text="$t('app.save')"
            icon="save"
            @click="submitForm('ruleForm')"
            :loading="isGeneralLoading"
            :disabled="isGeneralLoading"
          />
        </template>
      </base-footer>
    </template>
  </detail-layout>
</template>

<script>
import DetailLayout from '@/components/layout/DetailLayout.vue'
import AccessibleElSelect from '@/components/elementui/AccessibleElSelect.vue'
import { i18n } from '@/i18n'

import { getClients } from '@/services/clients/getClients'

import { getFlow } from '@/services/configurations/rules/getFlow'
import { newFlow } from '@/services/configurations/rules/newFlow'
import { updateFlow } from '@/services/configurations/rules/updateFlow'
import { getConfiguration } from '@/services/config/getConfiguration'
import { getConfigCorreosTags } from '@/services/clients/getConfigCorreosTags'

import { TIPOS_ESTADO_DESCARGA } from '@/config/tipos-estado-descarga'
import { email as emailRegex } from '@/utils/regex'

import { getAllCompanies } from '@/services/companies/getAllCompanies'
import { getSedesByEmpresa } from '@/services/app/getSedesByEmpresa'
import { getOrganismosAll } from '@/services/configurations/getOrganismosAll'
import { getGroupsSimple } from '@/services/configurations/groups/getGroupsSimple'

import TextEditor from '@/components/texteditor/TextEditor.vue'

function isValidEmail (value) {
  return value.split(';').every(item => emailRegex.test(item))
}
function checkValidTemporizador (value) {
  if (Number.isInteger(parseFloat(value))) {
    if (value >= 1 && value <= 30) {
      return true
    }
  }
  return false
}

export default {
  /* eslint-disable camelcase */
  name: 'flow-detail',
  components: {
    DetailLayout,
    AccessibleElSelect,
    TextEditor,
  },
  data: function () {
    return {
      isMounted: false,
      dateRangeOptions: {
        firstDayOfWeek: 1,
      },
      searchView: false,
      activeName: 'es',
      localUserId: null,
      header: {
        title: 'DETALLE DE FLUJO AUTOMÁTICO DE AVISOS Y ESTADOS',
        icon: 'config',
        breadcrumb: {},
        nav: [],
        url: 'configurations/configflow'
      },
      loadingFirstTime: false,
      maxLengthCorreos: 500,
      tiposEstadoDescarga: TIPOS_ESTADO_DESCARGA,
      firstLoad: true,
      tooltipTemporizadorDias: this.$i18n.t('validation.configflow.temporizador'),
      form: this.getDefaultForm(),
      contentTextEditorCuerpoCorreo: '',
      tagsCuerpoCorreo: [],
      clientes: [],
      companies: [],
      sedes: [],
      organismos: [],
      loadingOrganismos: false,
      listaGroups: [],
      values: [
        {
          value: true,
          label: i18n.t('app.yes')
        },
        {
          value: false,
          label: i18n.t('app.no')
        }
      ],
      rulesFlow: {
        clienteId: [
          { validator: this.isGenericValidation, trigger: 'blur' },
          { validator: this.isGenericValidation, trigger: 'change' }
        ],
        Nombre: [
          { validator: this.isGenericValidation, trigger: 'blur' }
        ],
        TemporizadorDias: [
          { validator: this.isGenericValidation, trigger: 'blur' },
          { required: true, validator: this.isTemporizadorValid, trigger: 'blur' }
        ],
        idEstadoInicial: [
          { validator: this.isGenericValidation, trigger: 'blur' },
          { validator: this.isGenericValidation, trigger: 'change' }
        ],
        EmailGenerico: [
          { required: false, validator: this.isEmailValid, trigger: 'blur' }
        ],
        AsuntoCorreo: [
          { validator: this.isGenericValidation, trigger: 'blur' },
        ],
      },
    }
  },
  computed: {
    empresaFilter () {
      return this.isCreate ? this.$store.getters['configflow/getMasterFilters']?.empresa?.value || null : null
    },
    disableSelectsGeneral () {
      //       :isDisabled="isSelectClientEmpty || (isCreate && role !== 'Administrador')"
      if (this.role === 'Administrador' && this.isCreate) return false
      if (this.isSelectClientEmpty) return true
      return false
    },
    listaGroupsGroupingOwner () {
      return this.listaGroupsGrouping.filter(x => x.label !== 'Reparto')
    },
    listaGroupsGrouping () {
      if (!this.isEmptyList) {
        const options = []
        this.listaGroups.forEach(x => {
          if (options.filter(y => y.label === x.group).length === 0) {
            options.push(
              { label: x.group, options: [] }
            )
          }
        })
        this.listaGroups.forEach(x => {
          const index = options.map(x => x.label).indexOf(x.group)
          options[index].options.push(
            {
              id: x.id,
              name: x.value,
            }
          )
        })
        return options
      }
      return []
    },
    gettextCuerpoCorreo () {
      if (!this.isMounted) return
      return this.$refs.textEditorCuerpoCorreo.getContent()
    },
    estadoInicialSelected () {
      if (this.form.idEstadoInicial === null || this.form.idEstadoInicial === undefined) return null
      return this.tiposEstadoDescarga.filter(x => x.id === this.form.idEstadoInicial)[0]
    },
    estadoInicialSinDestino () {
      if (!this.estadoInicialSelected) return false
      return this.estadoInicialSelected.FlujoSinDestino
    },
    tiposEstadosEntrada () {
      return this.tiposEstadoDescarga
    },
    tiposEstadosSalida () {
      if (!this.estadoInicialSelected ||
        this.form.idEstadoInicial === null || 
        this.form.idEstadoInicial === undefined ||
        this.estadoInicialSinDestino) { 
        return [] 
      }
      let res = this.tiposEstadoDescarga
      res = res
        .filter(x => x.id !== this.estadoInicialSelected.id)
        .filter(x => !x.FlujoSalidaoDeshabilitado)
        .filter(x => x.TipoEstadoFlujo === this.estadoInicialSelected.TipoEstadoFlujo)

      return res
    },
    isGeneralLoading: function () {
      return this.$store.getters.isLoading
    },
    id: function () {
      return parseInt(this.$route.params.id)
    },
    isCreate: function () {
      return this.id === -1 || this.id === '-1'
    },
    role: function () {
      return this.$store.getters['auth/role']
    },
    hasModuleFlujoAceptacion () {
      return this.$store.getters['auth/hasModuleFlujoAceptacion']
    },
    isSelectClientEmpty: function () {
      return this.form.clienteId === null || this.form.clienteId === undefined || this.form.clienteId === ''
    },
    isSelectEmpresaEmpty: function () {
      return this.form.EmpresaID === null || this.form.EmpresaID === undefined || this.form.EmpresaID === ''
    },
    isSelectGroupEmpty () {
      return this.form.grupoPropietario === null || this.form.grupoPropietario === undefined || this.form.grupoPropietario === ''
    },
    clientesForAccessibleSelect () {
      return this.clientes.map(cliente => ({
        id: cliente.IdEmpresa,
        value: cliente.IdEmpresa,
        name: cliente.Nombre
      }))
    },
    tiposEstadosEntradaForAccessibleSelect () {
      return this.tiposEstadosEntrada.map(estado => ({
        id: estado.id,
        value: estado.id,
        name: estado.Nombre
      }))
    },
    tiposEstadosSalidaForAccessibleSelect () {
      return this.tiposEstadosSalida.map(estado => ({
        id: estado.id,
        value: estado.id,
        name: estado.Nombre
      }))
    },
    companiesForAccessibleSelect () {
      return this.companies.map(empresa => ({
        id: empresa.value,
        value: empresa.value,
        name: empresa.label
      }))
    },
    sedesForAccessibleSelect () {
      return this.sedes.map(sede => ({
        id: sede.value,
        value: sede.value,
        name: sede.label
      }))
    },
    organismosForAccessibleSelect () {
      return this.organismos.map(organismo => ({
        id: organismo.id,
        value: organismo.id,
        name: organismo.label
      }))
    },
  },
  watch: {
    form: {
      deep: true,
      immediate: true,
      handler: 'watchFormHandler'
    },
    'form.idEstadoInicial': function (value, oldValue) {
      if (!this.loadingFirstTime) { 
        this.form.idEstadoDestino = null 
      }
    },
    'form.clienteId': {
      deep: true,
      handler: 'watchclienteIdHandler'
    },
    gettextCuerpoCorreo: {
      deep: true,
      immediate: true,
      handler: 'watchFormHandler'
    },
  },
  created: async function () {
    const breadcrumbs = []

    if (this.role === 'AdminDistribuidor' || this.role === 'SuperAdminDistribuidor' || this.role === 'SuperAdministrador') {
      const dataClients = await getClients()
      if (dataClients.blnError === true) {
        this.doFeedback(dataClients.msgError + ' [Código de error: ' + dataClients.codError + ']')
      }
      this.clientes = dataClients.Empresas
      breadcrumbs.push({
        path: this.role === 'SuperAdministrador' ? 'configurationssa' : 'configurations',
        name: this.$i18n.t('app.configurations')
      })
      if (this.isCreate) this.preSelectClient(this.form, 'clienteId')
    } else {
      breadcrumbs.push({
        path: 'configurations',
        name: this.$i18n.t('app.configurations')
      })
    }
    breadcrumbs.push({ path: 'configurations/configflow', name: this.$i18n.t('app.configflow') })

    if (!this.isCreate) {
      this.loadingFirstTime = true
      await this.downloadData(this.id)
      breadcrumbs.push({ path: '', name: this.form.Nombre })
      this.$store.commit('app/setheaderBreadCrumb', this.form.Nombre)
    } else {
      await this.resetAsuntoCuerpo()
      if (this.role === 'Administrador') {
        this.watchclienteIdHandler()
      }

      breadcrumbs.push({
        path: '',
        name: this.$i18n.t('configflow.new')
      })
    }
    this.header.breadcrumb = breadcrumbs

    const dataCorreosTags = await this.downloadDataCorreosTags(this.id)
    this.setDataCorreosTags(dataCorreosTags.Tags)
    this.$store.commit('setIsLoading', false)
  },
  mounted () {
    this.isMounted = true
    this.resetControlFormChanges()
  },
  methods: {
    resetAsuntoCuerpo: async function () {
      const dataAsunto = await getConfiguration({ Campo: 'AsuntoCorreoFlujoDefecto' })
      this.form.AsuntoCorreo = dataAsunto.Respuesta
      const dataCuerpo = await getConfiguration({ Campo: 'CuerpoCorreoFlujoDefecto' })
      this.contentTextEditorCuerpoCorreo = dataCuerpo.Respuesta
    },  
    isEmailValid: function (rule, value, callback) {
      if (!value) {
        callback() // queremos que la validación sea opcional en este caso.
      } else if (!isValidEmail(value)) {
        this.doAriaLiveAndReturnCallback(i18n.t('validation.email_multiple'), callback)
      } else if (value.length > this.maxLengthCorreos) {
        this.doAriaLiveAndReturnCallback(i18n.t('validation.email_length') + this.maxLengthCorreos + ' caracteres', callback)
      } else {
        callback()
      }
    },
    watchFormHandler () {
      this.$store.commit('app/setFormChanges', true)
    },
    isTemporizadorValid: function (rule, value, callback) {
      if (!value) {
        callback() // queremos que la validación sea opcional en este caso.
      } else if (!checkValidTemporizador(value)) {
        this.doAriaLiveAndReturnCallback(i18n.t('validation.configflow.temporizador'), callback)
      } else {
        callback()
      }
    },
    getDefaultForm: function () {
      return {
        id: 0,
        clienteId: null,
        EmpresaID: null,
        SedeID: null,
        OrganismoID: null,
        grupoPropietario: null,
        Nombre: '',
        TemporizadorDias: 3,
        Activo: true,
        idEstadoInicial: null,
        EnviarCorreosPrincipales: false,
        EnviarCorreosSecundarios: false,
        EmailGenerico: '',
        idEstadoDestino: null,
        AsuntoCorreo: '',
      }
    },
    watchclienteIdHandler: async function () {
      this.form.EmpresaID = null
      this.form.SedeID = null
      this.form.grupoPropietario = null
      this.companies = []
      this.sedes = []
      this.listaGroups = []
      if (!this.isSelectClientEmpty || (this.isCreate && this.role === 'Administrador')) {
        const data = await getAllCompanies({ idEmpresaCert: this.form.clienteId })
        if (data.blnError) {
          this.doFeedback(data.msgError + ' [Código de error: ' + data.codError + ']')
        } else {
          data.CIFSEmpresa.forEach(row => {
            this.companies.push({ value: row.IdCIFEmpresa, label: row.CIF + ' - ' + row.NombreCIF })
          })
        }
        const dataSedes = await getSedesByEmpresa({ idCliente: this.form.clienteId })
        if (dataSedes.blnError) {
          this.doFeedback(dataSedes.msgError + ' [Código de error: ' + dataSedes.codError + ']')
        } else {
          dataSedes.PGNS.forEach(row => {
            this.sedes.push({ value: row.IdPGN, label: row.Nombre })
          })
        }
        const dataGrupos = await getGroupsSimple({ idCliente: this.form.clienteId })
        if (dataGrupos.blnError) {
          this.doFeedback(dataGrupos.msgError + ' [Código de error: ' + dataGrupos.codError + ']')
        } else {
          dataGrupos.Respuesta.forEach(row => {
            this.listaGroups.push({ id: row.IdGrupo, value: row.Nombre, group: row.Tipo })
          })
        }
      }
    },
    remoteOrganismos: async function (query) {
      this.organismos = []
      if (query !== '' && this.loadingOrganismos === false && query.length > 2) {
        this.loadingOrganismos = true
        setTimeout(async () => {
          const organismos = await getOrganismosAll({ filtro: query })
          if (organismos.blnError === true) {
            this.doFeedback(organismos.msgError + ' [Código de error: ' + organismos.codError + ']')
          }
          if (organismos.DatosOrganismos !== undefined && organismos.DatosOrganismos?.length > 0) {
            let organismosMatch = []
            if (organismos.DatosOrganismos.length > 200) {
              organismosMatch = organismos.DatosOrganismos.filter(x => x.CIF.toUpperCase() === query.toUpperCase() || x.Nombre.toUpperCase() === query.toUpperCase())
              if (organismosMatch.length > 0) {
                this.doFeedback('Se han encontrado resultados coincidentes, pero se ha excedido el límite para los que contienen la búsqueda. Si el resultado esperado no aparece, introduzca más texto para acortar la búsqueda.', 'info', 5000)
              } else {
                this.doFeedback('Se ha excedido el límite de resultados en la búsqueda. Por favor, introduzca más texto para acortar la búsqueda.', 'info', 5000)
              }
            } else {
              organismosMatch = organismos.DatosOrganismos
            }
            organismosMatch.forEach(element => {
              this.organismos.push(
                { 
                  id: element.idOrganismo, 
                  value: element.Nombre,
                  label: this.formatNombreOrg(element),
                }
              )
            })
          }
          this.loadingOrganismos = false
        }, 200)
      }
    },
    formatNombreOrg (element) {
      return element.Nombre + ' - ' + element.CIF
    },
    goDetailClient () {
      const id = this.form.clienteId
      this.$router.push({ name: 'configurations/clients/details', params: { id } })
    },
    toggleSearchView: function () {
      this.searchView = !this.searchView
    },
    submitForm: function (formName) {
      if (this.isCreate) {
        this.newFlowHandler(formName)
      } else {
        this.updateFlowHandler(formName)
      }
    },
    newFlowHandler: function (formName) {
      this.$store.commit('setAjaxRequestPending', true)
      this.$store.commit('setIsLoading', true)
      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          const params = {
            idEmpresa: this.form.clienteId,
            idCIFEmpresa: this.form.EmpresaID,
            idPGN: this.form.SedeID,
            idGrupoPropietario: this.form.grupoPropietario,
            idOrganismo: this.form.OrganismoID,
            Nombre: this.form.Nombre,
            TemporizadorDias: this.form.TemporizadorDias,
            Activo: this.form.Activo,
            idEstadoInicial: this.form.idEstadoInicial,
            EnviarCorreosPrincipales: this.form.EnviarCorreosPrincipales,
            EnviarCorreosSecundarios: this.form.EnviarCorreosSecundarios,
            EmailGenerico: this.form.EmailGenerico,
            idEstadoDestino: this.form.idEstadoDestino,
            CuerpoCorreo: this.gettextCuerpoCorreo,
            AsuntoCorreo: this.form.AsuntoCorreo
          }
          const response = await newFlow(params)
          if (response.blnError === true) {
            this.doFeedback(response.msgError + ' [Código de error: ' + response.codError + ']')
          } else {
            this.doFeedback('Datos guardados correctamente', 'success')
            this.resetForm()
            this.form = this.getDefaultForm()
            await this.resetAsuntoCuerpo()
            this.$refs.textEditorCuerpoCorreo.reloadContent()
            this.resetControlFormChanges()
            if (this.role === 'AdminDistribuidor' || this.role === 'SuperAdminDistribuidor' || this.role === 'SuperAdministrador') {
              this.preSelectClient(this.form, 'clienteId')
            }
          }
        } else {
          this.doFeedback('Error al procesar los datos, revise la información')
        }
        this.$store.commit('setIsLoading', false)
      })
    },
    resetForm () {
      this.$refs.ruleForm.resetFields()
    },
    updateFlowHandler: function (formName) {
      this.$store.commit('setAjaxRequestPending', true)
      this.$store.commit('setIsLoading', true)
      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          const params = {
            idFlujoAutomatico: this.form.id,
            idCIFEmpresa: this.form.EmpresaID,
            idPGN: this.form.SedeID,
            idGrupoPropietario: this.form.grupoPropietario,
            idOrganismo: this.form.OrganismoID,
            Nombre: this.form.Nombre,
            TemporizadorDias: this.form.TemporizadorDias,
            Activo: this.form.Activo,
            idEstadoInicial: this.form.idEstadoInicial,
            EnviarCorreosPrincipales: this.form.EnviarCorreosPrincipales,
            EnviarCorreosSecundarios: this.form.EnviarCorreosSecundarios,
            EmailGenerico: this.form.EmailGenerico,
            idEstadoDestino: this.form.idEstadoDestino,
            CuerpoCorreo: this.gettextCuerpoCorreo,
            AsuntoCorreo: this.form.AsuntoCorreo
          }
          const response = await updateFlow(params)
          if (response.blnError === false) {
            this.doFeedback('Datos guardados correctamente', 'success')
            this.updateView()
            this.$store.commit('setAjaxRequestPending', false)
            this.resetControlFormChanges()
          } else {
            this.doFeedback(response.msgError + ' [Código de error: ' + response.codError + ']')
          }
        } else {
          this.doFeedback('Error al procesar los datos, revise la información')
        }
        this.$store.commit('setIsLoading', false)
      })
    },
    goBack: function () {
      this.$router.push({ name: 'configurations/configflow' })
    },
    downloadDataCorreosTags: async function (id) {
      try {
        const template = await getConfigCorreosTags()
        if (template.blnError === true) {
          this.doFeedback(template.msgError + ' [Código de error: ' + template.codError + ']')
        } else {
          return template
        }
      } catch (error) {
        this.doFeedback()
      } 
      return null
    },
    downloadData: async function (id) {
      let data = null
      try {
        this.$store.commit('setIsLoading', true)
        const params = {
          idFlujoAutomatico: id
        }
        data = await getFlow(params)
        this.setFlow(data.Respuesta)
      } catch (error) {
        console.error(error)
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
      return data
    },
    setFlow: function (data) {
      this.form.id = data.idFlujoAutomatico
      this.form.clienteId = data.idEmpresa
      
      setTimeout(() => {
        this.form.EmpresaID = data.idCIFEmpresa
        this.form.grupoPropietario = data.idGrupoPropietario
        this.form.SedeID = data.idPGN
        if (data.Organismo) {
          this.organismos.push(
            { 
              id: data.Organismo.idOrganismo, 
              value: data.Organismo.Nombre,
              label: this.formatNombreOrg(data.Organismo),
            }
          )
          setTimeout(() => {
            this.form.OrganismoID = data.Organismo.idOrganismo
            this.resetControlFormChanges()
          }, 200)
        }
      }, 200)

      this.form.Nombre = data.Nombre
      this.form.TemporizadorDias = data.TemporizadorDias
      this.form.Activo = data.Activo
      this.form.idEstadoInicial = data.idEstadoInicial
      this.form.EnviarCorreosPrincipales = data.EnviarCorreosPrincipales
      this.form.EnviarCorreosSecundarios = data.EnviarCorreosSecundarios
      this.form.EmailGenerico = data.EmailGenerico
      this.form.idEstadoDestino = data.idEstadoDestino

      this.form.AsuntoCorreo = data.AsuntoCorreo
      this.contentTextEditorCuerpoCorreo = data.CuerpoCorreo

      setTimeout(() => {
        this.loadingFirstTime = false
      }, 100)
      this.resetControlFormChanges()
    },
    setDataCorreosTags: function (data) {
      if (data) {
        // Tags
        this.tagsCuerpoCorreo = data.filter(x => x.Nombre === 'AvisosFlujosAutomaticos')[0].Tags
      }
      this.resetControlFormChanges()
    },
    updateView: function () {
      this.header.breadcrumb[2].name = this.form.Nombre
      this.$store.commit('app/setheaderBreadCrumb', this.form.Nombre)
    },
  }
}
</script>

<style scoped>
.box {
  background-color: #444;
  color: #fff;
  border-radius: 5px;
  border: 1px solid black;
  padding: 5px 20px;
}
.grid {
  display: grid;
  grid-gap: 10px;
}
.wrapper {
  grid-template-columns: 1fr 1fr;
  background-color: seagreen;
  padding: 10px;
  width: 50%;
  margin: 20px auto;
}
.grid.left {
  grid-column: 1 / span 1;
  grid-auto-rows: min-content;
}
.grid.right {
  grid-column: span 1 / -1;
  grid-auto-rows: min-content;
}
.tip {
  grid-column: 1 / -1;
}
.pic > img {
  max-width: 100%;
  max-height: 100%;
}
</style>

<style lang="sass" scoped>
</style>
